'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { RotateCcw, X } from '@/components/ui/icons'
import { BUILD_SHA, isStaleBuild } from '@/lib/buildSha'

/** Checks run on return to the tab, at most this often, plus a slow heartbeat while it stays in front. */
const MIN_CHECK_GAP_MS = 60 * 1000
const HEARTBEAT_MS = 15 * 60 * 1000

async function fetchLiveBuildSha(): Promise<string> {
  const response = await fetch('/api/build', { cache: 'no-store' })
  if (!response.ok) return ''
  const payload = (await response.json().catch(() => null)) as { sha?: unknown } | null
  return typeof payload?.sha === 'string' ? payload.sha : ''
}

/**
 * Canvas tabs stay open for days, and the frontend and backend deploy apart:
 * a tab running an older bundle keeps calling a newer API until someone
 * reloads it. This asks for that reload once the site is on another commit.
 * Dismiss hides it until the next commit; a quiet failure to check is not a
 * reason to prompt.
 */
export function StaleBuildPrompt({ builtSha = BUILD_SHA }: { builtSha?: string }) {
  const [liveSha, setLiveSha] = useState('')
  const [dismissedFor, setDismissedFor] = useState<string | null>(null)
  const lastCheckedAt = useRef(0)
  // Checks can overlap (a focus check and the heartbeat); only the newest-started one may write.
  const latestCheck = useRef(0)

  useEffect(() => {
    if (!builtSha) return
    let live = true
    const check = async (force = false) => {
      const now = Date.now()
      if (!force && now - lastCheckedAt.current < MIN_CHECK_GAP_MS) return
      lastCheckedAt.current = now
      const seq = ++latestCheck.current
      const sha = await fetchLiveBuildSha().catch(() => '')
      if (live && sha && seq === latestCheck.current) setLiveSha(sha)
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    const heartbeat = window.setInterval(() => void check(true), HEARTBEAT_MS)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      live = false
      window.clearInterval(heartbeat)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [builtSha])

  if (!isStaleBuild({ built: builtSha, live: liveSha }) || dismissedFor === liveSha) return null

  return (
    <div className="fixed inset-x-3 bottom-3 z-[1000] sm:left-auto sm:right-4 sm:w-[360px]" role="status">
      <div className="rounded-md border border-border bg-popover p-3 text-foreground shadow-lg">
        <div className="flex items-start gap-3">
          <RotateCcw className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" strokeWidth={1.75} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Martini has updated</p>
            <p className="mt-1 text-xs text-muted-foreground">Reload to keep this tab in step with the server.</p>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setDismissedFor(liveSha)}
            className="flex h-6 w-6 flex-none items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" className="h-8 text-xs" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      </div>
    </div>
  )
}
